/**
 * Converging Letters View (Web)
 *
 * Renders chromatic aberration letters that converge from chaos into words.
 * Shared by WordConvergenceAnimation and FullExperienceAnimation.
 */

import { memo } from 'react';
import { View, Text } from 'react-native';
import type { AnimationPhase } from './types';
import type { ConvergingLetterState } from './animationHelpers.web';
import { styles } from './animationStyles.web';

interface ConvergingLettersViewProps {
  letters: ConvergingLetterState[];
  lineComplete: boolean;
  phase: AnimationPhase;
}

const ConvergingLettersView = memo(function ConvergingLettersView({
  letters,
  lineComplete,
  phase,
}: ConvergingLettersViewProps) {
  return (
    <View style={styles.convergenceZone}>
      {letters.map((letter, idx) => {
        const isVisible = !lineComplete || phase === 'emergence';
        if (!isVisible) return null;

        return (
          <View
            key={`conv-letter-${idx}`}
            style={[
              styles.convergingLetter,
              {
                transform: [
                  { translateX: letter.currentX },
                  { translateY: letter.currentY },
                  { scale: letter.scale },
                ],
                opacity: letter.opacity,
              },
            ]}
          >
            <Text
              style={[
                styles.chromaticLayerR,
                { transform: [{ translateX: -letter.chromaOffset }] },
              ]}
            >
              {letter.char}
            </Text>
            <Text
              style={[
                styles.chromaticLayerG,
                { transform: [{ translateY: letter.chromaOffset * 0.5 }] },
              ]}
            >
              {letter.char}
            </Text>
            <Text
              style={[
                styles.chromaticLayerB,
                { transform: [{ translateX: letter.chromaOffset }] },
              ]}
            >
              {letter.char}
            </Text>
            <Text style={styles.mainLetter}>{letter.char}</Text>
          </View>
        );
      })}
    </View>
  );
});

export default ConvergingLettersView;
